import React, { useEffect, useMemo, useState } from "react";
import { format, startOfWeek, addDays } from "date-fns";
import es from "date-fns/locale/es";
import NavigationWeek from "./NavigationWeek";
import AdminCalendarWeekView from "./AdminCalendarWeekView";
import AdminGuardiasDetailModal from "./AdminGuardiasDetailModal";
import { fetchDetalleGuardia, fetchFranjasHorarias } from "./Guardias-Api";

const AdminGuardiasDashboard = ({ setTitulo }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [franjasHorarias, setFranjasHorarias] = useState([]);
  const [detallesSemana, setDetallesSemana] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detalleModal, setDetalleModal] = useState({
    open: false,
    fecha: null,
    franja: null,
    detalle: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (setTitulo) {
      setTitulo("Guardias Admin");
    }
  }, [setTitulo]);

  useEffect(() => {
    fetchFranjasHorarias(setFranjasHorarias);
  }, []);

  useEffect(() => {
    if (franjasHorarias.length === 0) {
      return;
    }

    const cargarSemana = async () => {
      setLoading(true);
      setError(null);
      const weekStart = startOfWeek(currentDate, { locale: es, weekStartsOn: 1 });
      const dias = Array.from({ length: 5 }, (_, idx) => addDays(weekStart, idx));
      const map = {};
      const requests = [];

      dias.forEach((dia) => {
        const fechaStr = format(dia, "yyyy-MM-dd");
        franjasHorarias.forEach((franja) => {
          const key = `${fechaStr}|${franja.id}`;
          const request = fetchDetalleGuardia(franja.id, fechaStr)
            .then((data) => {
              map[key] = data;
            })
            .catch(() => {
              map[key] = { error: true };
            });
          requests.push(request);
        });
      });

      try {
        await Promise.all(requests);
        setDetallesSemana(map);
      } catch (err) {
        setError("No se pudo cargar el cuadrante de guardias");
      } finally {
        setLoading(false);
      }
    };

    cargarSemana();
  }, [currentDate, franjasHorarias]);

  const handleNavigateWeek = (offset) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7 * offset);
      return next;
    });
  };

  const handleOpenDetalle = async (date, franja) => {
    const fechaStr = format(date, "yyyy-MM-dd");
    const key = `${fechaStr}|${franja.id}`;
    const existente = detallesSemana[key];

    setDetalleModal({
      open: true,
      fecha: fechaStr,
      franja,
      detalle: existente && !existente.error ? existente : null,
      loading: !existente || existente.error,
      error: null,
    });

    if (existente && !existente.error) {
      return;
    }

    try {
      const data = await fetchDetalleGuardia(franja.id, fechaStr);
      setDetallesSemana((prev) => ({ ...prev, [key]: data }));
      setDetalleModal((prev) => ({
        ...prev,
        detalle: data,
        loading: false,
      }));
    } catch (err) {
      setDetalleModal((prev) => ({
        ...prev,
        loading: false,
        error: "No se pudo cargar el detalle",
      }));
    }
  };

  const handleCloseDetalle = () => {
    setDetalleModal({
      open: false,
      fecha: null,
      franja: null,
      detalle: null,
      loading: false,
      error: null,
    });
  };

  const resumen = useMemo(() => {
    const totalAusencias = Object.values(detallesSemana).reduce((acc, item) => {
      if (!item || item.error) return acc;
      return acc + (item.ausencias?.length || 0);
    }, 0);
    return totalAusencias;
  }, [detallesSemana]);

  return (
    <div className="container mt-4">
      <h2 style={{ textAlign: "center" }}>Cuadrante de Guardias (Admin)</h2>
      <NavigationWeek currentDate={currentDate} onNavigate={handleNavigateWeek} />

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="alert alert-info">Cargando cuadrante...</div>}

      <AdminCalendarWeekView
        currentDate={currentDate}
        timeSlots={franjasHorarias}
        detallesSemana={detallesSemana}
        loading={loading}
        onOpenDetalle={handleOpenDetalle}
      />

      <div className="mt-3 text-muted">
        Total de ausencias en la semana: <strong>{resumen}</strong>
      </div>

      <AdminGuardiasDetailModal
        isOpen={detalleModal.open}
        onClose={handleCloseDetalle}
        fecha={detalleModal.fecha}
        franja={detalleModal.franja}
        detalle={detalleModal.detalle}
        loading={detalleModal.loading}
        error={detalleModal.error}
      />
    </div>
  );
};

export default AdminGuardiasDashboard;
